import React, { useEffect, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import Header from '../components/Header';
import Sidebar, {
    SIDEBAR_COLLAPSED_WIDTH,
    SIDEBAR_EXPANDED_WIDTH,
} from '../components/Sidebar';
import { useSessionManager } from '../hooks/useSessionManager';
import { motion, AnimatePresence } from 'framer-motion';
import { useDrawer } from '../services/providers/DrawerContext';
import { Outlet, useLocation } from 'react-router';
import ErrorBoundary from '../components/ErrorBoundary';
import { useMixpanelPageTracking } from '../hooks/useMixpanelPageTracking';
import { useAcademicYearSync } from '../hooks/useAcademicYearSync';
import HistoricalYearBanner from '../components/AcademicYearSelector/HistoricalYearBanner';
import { getEnvironment, isProduction, Environment } from '../lib/environment';
import { EmergencyPopupOverlay } from '../components/Communication/EmergencyPopupOverlay';
import { useEmergencyPopups } from '../hooks/useEmergencyPopups';
import { useMarkAsRead } from '../hooks/useNotifications';
import { useCommunicationHub } from '../hooks/useCommunicationHub';
import { AnnouncementModal } from '../components/Communication/AnnouncementModal';
import { useNotificationSignalR } from '../hooks/useCommunication';
import { SupportWidget } from '../components/support/SupportWidget';
import { PrintSchoolHeader } from '../components/PrintSchoolHeader';

const DashboardLayout: React.FC = () => {
    const theme = useTheme();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useSessionManager();
    useMixpanelPageTracking();
    useAcademicYearSync();

    const { isConnected } = useCommunicationHub();
    useNotificationSignalR();

    const { currentPopup, dismissCurrentPopup } = useEmergencyPopups();
    const { mutate: markAsRead } = useMarkAsRead();
    const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
    const { open, toggleDrawer, setOpen } = useDrawer();

    useEffect(() => {
        if (isMobile) {
            setOpen(false);
        }
    }, [isMobile, setOpen]);

    useEffect(() => {
        if (isConnected) {
            console.log('[DashboardLayout] SignalR connected - Real-time notifications enabled');
        }
    }, [isConnected]);

    useEffect(() => {
        const handleGlobalShortcut = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
                e.preventDefault();
                setAnnouncementModalOpen(true);
            }
        };
        window.addEventListener('keydown', handleGlobalShortcut);
        return () => window.removeEventListener('keydown', handleGlobalShortcut);
    }, []);

    const handleDrawerToggle = () => {
        toggleDrawer();
    };

    const handleDismissPopup = (notificationId: string, dontShowAgain?: boolean) => {
        markAsRead(notificationId);
        dismissCurrentPopup(notificationId, dontShowAgain);
    };

    const env = getEnvironment();
    const isDevEnv = env === Environment.Development;
    const sidebarWidth = open ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }} className="print-shell">
            {/* Print-only overrides: undo the fixed-height/overflow-hidden/absolute-positioning chain
                this layout relies on for scrolling, and hide chrome that shouldn't appear on a printed
                page. Framer Motion's `style` prop can't take a `@media` key, so the scrolling content
                wrapper below is targeted by class name here instead of via `sx`. */}
            <style>{`
                @media print {
                    .print-hide { display: none !important; }
                    .print-shell { height: auto !important; overflow: visible !important; }
                    .print-main { width: 100% !important; overflow: visible !important; }
                    .print-scroll-area { overflow: visible !important; height: auto !important; }
                    .print-content { position: static !important; overflow: visible !important; height: auto !important; }
                    .print-content-inner { margin-top: 0 !important; }
                    .no-print { display: none !important; }
                    /* Fallback for a native Ctrl+P (or right-click > Print) that bypasses a
                       page's own Print button/portal entirely — this covers whatever the
                       browser is asked to print without any JS involved. Target MUI's own
                       Drawer root class directly rather than relying on an ancestor wrapper:
                       the Sidebar's mobile ("temporary") variant renders via a MUI Modal,
                       which portals its DOM straight onto <body> regardless of where
                       <Sidebar> sits in the React tree, so a wrapping div around it here has
                       no effect on it — only a global class selector like this reaches it. */
                    .MuiDrawer-root { display: none !important; }
                    .print-only-school-header { display: block !important; }
                }
            `}</style>
            {currentPopup && (
                <EmergencyPopupOverlay
                    notification={currentPopup}
                    onDismiss={handleDismissPopup}
                />
            )}

            {announcementModalOpen && (
                <AnnouncementModal
                    open={announcementModalOpen}
                    onClose={() => setAnnouncementModalOpen(false)}
                />
            )}

            {!isProduction() && (
                <Box
                    className="print-hide"
                    sx={{
                        width: '100%',
                        height: '28px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        background: isDevEnv
                            ? 'linear-gradient(90deg, #064e3b 0%, #065f46 40%, #047857 60%, #064e3b 100%)'
                            : 'linear-gradient(90deg, #78350f 0%, #92400e 40%, #b45309 60%, #78350f 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'envBannerShimmer 4s linear infinite',
                        zIndex: (t) => t.zIndex.drawer + 10,
                        '@keyframes envBannerShimmer': {
                            '0%': { backgroundPosition: '0% 0%' },
                            '100%': { backgroundPosition: '200% 0%' },
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: isDevEnv ? '#6ee7b7' : '#fcd34d',
                            animation: 'envDotPulse 2s ease-out infinite',
                            '@keyframes envDotPulse': {
                                '0%': { boxShadow: isDevEnv ? '0 0 0 0 rgba(110,231,183,0.7)' : '0 0 0 0 rgba(252,211,77,0.7)' },
                                '70%': { boxShadow: isDevEnv ? '0 0 0 6px rgba(110,231,183,0)' : '0 0 0 6px rgba(252,211,77,0)' },
                                '100%': { boxShadow: isDevEnv ? '0 0 0 0 rgba(110,231,183,0)' : '0 0 0 0 rgba(252,211,77,0)' },
                            },
                        }}
                    />
                    <Box
                        component="span"
                        sx={{
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            letterSpacing: '0.15em',
                            textTransform: 'uppercase',
                            color: isDevEnv ? '#d1fae5' : '#fef3c7',
                            fontFamily: 'monospace',
                            userSelect: 'none',
                        }}
                    >
                        {isDevEnv ? 'Development' : 'Staging'} Environment
                    </Box>
                    {[0, 1, 2].map((i) => (
                        <Box
                            key={i}
                            sx={{
                                width: 3,
                                height: 3,
                                borderRadius: '50%',
                                backgroundColor: isDevEnv ? 'rgba(110,231,183,0.35)' : 'rgba(252,211,77,0.35)',
                                mx: 0.25,
                            }}
                        />
                    ))}
                    <Box
                        component="span"
                        sx={{
                            fontSize: '0.6rem',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            color: isDevEnv ? 'rgba(209,250,229,0.6)' : 'rgba(254,243,199,0.6)',
                            fontFamily: 'monospace',
                            userSelect: 'none',
                        }}
                    >
                        Not for production use
                    </Box>
                </Box>
            )}

            {/* Full-width top header */}
            <Box className="print-hide">
                <Header />
            </Box>

            <Box
                className="print-shell"
                sx={{
                    display: 'flex',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                <Box className="print-hide">
                    <Sidebar
                        mobileOpen={open}
                        handleDrawerToggle={handleDrawerToggle}
                    />
                </Box>
                <Box
                    component="main"
                    className="print-main"
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        width: {
                            xs: '100%',
                            md: `calc(100% - ${sidebarWidth}px)`,
                        },
                        transition: (t) =>
                            t.transitions.create('width', {
                                easing: t.transitions.easing.sharp,
                                duration: t.transitions.duration.enteringScreen,
                            }),
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        backgroundColor: 'customColors.background',
                    }}
                >
                    <Box
                        className="print-scroll-area"
                        sx={{
                            position: 'relative',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <AnimatePresence mode="sync">
                            <motion.div
                                key={location.pathname}
                                className="print-content"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.1 }}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Box
                                    className="print-content-inner"
                                    sx={{
                                        p: 2,
                                        height: '100%',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        overflowY: 'auto',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        minHeight: 0,
                                        backgroundColor: 'customColors.background',
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                        '&::-webkit-scrollbar': { display: 'none' },
                                    }}
                                >
                                    <Box className="print-only-school-header" sx={{ display: 'none' }}>
                                        <PrintSchoolHeader />
                                    </Box>
                                    <HistoricalYearBanner />
                                    <ErrorBoundary>
                                        <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column' }}>
                                            <Outlet />
                                        </Box>
                                    </ErrorBoundary>
                                </Box>
                            </motion.div>
                        </AnimatePresence>
                    </Box>
                </Box>
            </Box>
            <Box className="print-hide">
                <SupportWidget />
            </Box>
        </Box>
    );
};

export default DashboardLayout;
